import Card from 'components/card';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getBiTopProducts } from 'interfaces/bi';
import { formatCurrency, formatNumber } from './helpers';

/**
 * KPI #8 — Top products by revenue or quantity over the selected period.
 *
 * A horizontal bar chart of the top N products, with a metric toggle
 * (CA / quantité). Reads from `/bi/products/top`.
 */
function TopProducts(props: { from: string; to: string; limit?: number }) {
    const { from, to, limit = 10 } = props;
    const [metric, setMetric] = useState<'revenue' | 'qty'>('revenue');
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            const res = await getBiTopProducts({ from, to, metric, limit });
            if (!active) return;
            setRows(res?.data ?? []);
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, [from, to, metric, limit]);

    const series = useMemo(
        () => [
            {
                name: metric === 'revenue' ? 'CA' : 'Quantité',
                data: rows.map((r) =>
                    metric === 'revenue' ? Number(r.revenueHT) || 0 : Number(r.quantity) || 0,
                ),
            },
        ],
        [rows, metric],
    );

    const MAX_LABEL = 22;
    const truncateLabel = (s: string) =>
        s.length > MAX_LABEL ? s.slice(0, MAX_LABEL - 1) + '…' : s;

    const options: any = {
        chart: { type: 'bar', toolbar: { show: false } },
        colors: ['#0260CB'],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: rows.map((r) => truncateLabel(r.name || r.ref || '—')),
            labels: {
                style: { colors: '#A3AED0', fontSize: '10px' },
                formatter: (v: number) => formatNumber(v),
            },
        },
        yaxis: {
            labels: {
                style: { colors: '#A3AED0', fontSize: '11px' },
                maxWidth: 160,
            },
        },
        tooltip: {
            theme: 'dark',
            x: {
                // Show full name in tooltip even if label is truncated.
                formatter: (_: any, opts: any) => rows[opts.dataPointIndex]?.name || '—',
            },
            y: {
                formatter: (v: number) =>
                    metric === 'revenue' ? formatCurrency(v) : formatNumber(v, 2),
            },
        },
        grid: { show: false },
    };

    return (
        <Card extra="p-5 border h-full">
            <div className="mb-3 flex items-start justify-between">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">Top produits</h4>
                    <p className="text-sm text-gray-500">Classement sur la période</p>
                </div>
                <div className="flex gap-3 text-sm">
                    <button
                        onClick={() => setMetric('revenue')}
                        className={`cursor-pointer ${
                            metric === 'revenue' ? 'font-bold text-toola-500 underline' : 'text-gray-500'
                        }`}
                    >
                        CA
                    </button>
                    <button
                        onClick={() => setMetric('qty')}
                        className={`cursor-pointer ${
                            metric === 'qty' ? 'font-bold text-toola-500 underline' : 'text-gray-500'
                        }`}
                    >
                        Quantité
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex h-[300px] items-center justify-center">
                    <p className="text-gray-400">Chargement...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-[300px] flex-col items-center justify-center gap-1">
                    <p className="text-gray-400">Aucune donnée sur la période.</p>
                    <p className="text-xs text-gray-400">Si c'est la première utilisation, cliquez sur « Recalculer les agrégats ».</p>
                </div>
            ) : (
                <ReactApexChart options={options} series={series} type="bar" height={320} />
            )}
        </Card>
    );
}

export default TopProducts;
